import React from 'react';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import { PieChart, PieSeriesType, PieValueType, PieChartProps } from '@mui/x-charts';
import { MakeOptional } from '@mui/x-charts/models/helpers';
import { makeStyles } from 'tss-react/mui';
import Skeleton from '@mui/material/Skeleton';
import { useTheme } from '@mui/material';
import { numberFormatter } from '../../utils/numbers';
import type { GroupPart } from '../../utils/fleet-filters';

const useStyles = makeStyles()((theme) => ({
  scrollbar: {
    overflowY: 'auto',
    overflowX: 'hidden',
    height: '288px',
    alignItems: 'center',
    '&::-webkit-scrollbar': {
      width: theme.spacing(1.8),
    },
    '&::-webkit-scrollbar-track': {
      borderLeft: `${theme.spacing(1)} solid rgba(0, 0, 0, 0)`,
      backgroundClip: 'padding-box',
      backgroundColor: theme.palette.grey[100],
    },
    '&::-webkit-scrollbar-thumb': {
      borderLeft: `${theme.spacing(1)} solid rgba(0, 0, 0, 0)`,
      backgroundClip: 'padding-box',
      backgroundColor: theme.palette.grey[300],
    },
  },
}));

export interface PieSection {
  id: string;
  value: number;
  label: string;
  color: string;
  parts?: GroupPart[];
  rowCount?: number;
}

interface CustomProps extends Omit<PieChartProps, 'series'> {
  id?: string;
  yLabel?: string;
  parentHeight?: boolean;
  fullWidth?: boolean;
  label: string;
  data: PieSection[];
  mainCount?: string;
  labelFixed?: number;
}

const CustomPieChart: React.FC<CustomProps> = ({
  fullWidth,
  label,
  data,
  mainCount,
  labelFixed = 3,
  loading,
  parentHeight,
  height,
}) => {
  const { classes } = useStyles();
  const theme = useTheme();

  const totalAmount = data.reduce((acc, item) => acc + item.value, 0);

  const chartData: MakeOptional<PieSeriesType<MakeOptional<PieValueType, 'id'>>, 'type'>[] = [
    {
      data,
      innerRadius: 64,
      outerRadius: 95,
      paddingAngle: 0,
      cornerRadius: 4,
      startAngle: 0,
      endAngle: 360,
      cx: 124,
      cy: 124,
    },
  ];

  if (loading) {
    return (
      <Skeleton sx={{ width: '100%', height: parentHeight ? '100%' : height }} variant="rounded" />
    );
  }

  return (
    <Grid item sm={12} md={fullWidth ? 12 : 6} container>
      <Grid item xs={6}>
        <Stack direction={'column'} alignItems="center" gap={2}>
          <Stack direction="row" position="relative">
            <PieChart
              series={chartData}
              height={248}
              width={248}
              slotProps={{ legend: { hidden: true } }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                textAlign: 'center',
                transform: 'translateX(-45%) translateY(-30%)',
              }}
            >
              <Typography variant="h1">{mainCount ?? numberFormatter(totalAmount, 2)}</Typography>
            </Box>
          </Stack>
          <Typography>{label}</Typography>
        </Stack>
      </Grid>
      <Grid item xs={6}>
        <List className={classes.scrollbar}>
          {data.map((item) => (
            <ListItem key={item.id}>
              <Typography variant="body2" textOverflow="ellipsis" overflow="hidden">
                <span
                  style={{
                    width: 14,
                    height: 14,
                    display: 'inline-block',
                    borderRadius: '50%',
                    backgroundColor: item.color,
                    marginRight: theme.spacing(1),
                  }}
                ></span>
                {`${item.label} - ${numberFormatter(item.value, labelFixed)}`}
              </Typography>
            </ListItem>
          ))}
        </List>
      </Grid>
    </Grid>
  );
};

export default CustomPieChart;
